import z from "zod";
import { LimitTypeEnum, OrderStatusEnum, OrderTypeEnum } from "../domain/entities/OrdersEntity";
import { OrdersApiRepository } from "./OrdersApiRepository";
import { Badge } from "@/components/atoms/Badge";

export const OrderStatusOptions = [
    { label: OrderStatusEnum[OrderStatusEnum.REGISTRO], value: OrderStatusEnum.REGISTRO.toString() },
    { label: OrderStatusEnum[OrderStatusEnum.COMPLEMENTACION], value: OrderStatusEnum.COMPLEMENTACION.toString() },
    { label: OrderStatusEnum[OrderStatusEnum.FIN], value: OrderStatusEnum.FIN.toString() },
];

export const variantMap: Record<OrderStatusEnum, "info" | "warning" | "success"> = {
    [OrderStatusEnum.REGISTRO]: "info",
    [OrderStatusEnum.COMPLEMENTACION]: "warning",
    [OrderStatusEnum.FIN]: "success",
};

export const columns = [
    {
        header: "Estatus",
        accessor: "status",
        render: (value: string) => {
            const variant = variantMap[value as unknown as OrderStatusEnum] ?? "info";
            return <Badge variant={variant}>{value.charAt(0)}</Badge>;
        },
        filterValue: (value: string) => value
    },
    { header: "Portafolio", accessor: "portfolio.name" },
    {
        header: "Tipo",
        accessor: "orderType",
        render: (value: string) => {
            const variant = value === OrderTypeEnum.COMPRA ? "darkInfo" : "darkError";
            return (
                <Badge variant={variant} className="w-full">
                    {`${value}`}
                </Badge>
            );
        }
    },
    { header: "Instrumento", accessor: "instrument.mnemonic" },
    {
        header: "Tipo Orden",
        accessor: "limitType",
        render: (value: string) => {
            const variant = value === LimitTypeEnum.MERCADO ? "primary" : "warning";
            return (
                <Badge variant={variant} className="w-full">
                    {`${value}`}
                </Badge>
            );
        }
    },
    {
        header: "Cantidad",
        accessor: "quantity",
        render: (value: number) =>
            new Intl.NumberFormat("es-ES", {
                style: "decimal",
                minimumFractionDigits: 2,
            }).format(value),
    },
    {
        header: "Precio límite",
        accessor: "limitPrice",
        render: (value: number) =>
            new Intl.NumberFormat("es-ES", {
                style: "decimal",
                minimumFractionDigits: 2,
            }).format(value),
    },
    {
        header: "Valor",
        accessor: "value",
        render: (value: number) =>
            `$${value.toLocaleString("es-CO",
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
            )}`
    },
    {
        header: "Creado",
        accessor: "createdAt",
        render: (value: Date) => value.toDateString(),
    },
];

export const orderTypeOptions = [
    { label: OrderTypeEnum[OrderTypeEnum.COMPRA], value: OrderTypeEnum.COMPRA.toString() },
    { label: OrderTypeEnum[OrderTypeEnum.VENTA], value: OrderTypeEnum.VENTA.toString() },
];

export const schema = z.object({
    id: z.string().optional(),
    portfolio: z.string().min(1, "El portafolio es obligatorio"),
    orderType: z.boolean(),
    instrument: z.string().min(1, "El instrumento es obligatorio"),
    quantity: z.number({ invalid_type_error: "La cantidad debe ser un número" })
        .min(1, "Debe ser mayor a 0"),
    limitPrice: z.number({ invalid_type_error: "El límite debe ser un número" }),
    limitType: z.boolean(),
    value: z.number({ invalid_type_error: "El valor debe ser un número" }),
    confirmNumber: z.string().min(1, "El número de operación es obligatorio").optional(),
    status: z.string().optional()
}).superRefine((data, ctx) => {
    // Validaciones dinámicas según limitType
    if (!data.limitType) {
        if (data.limitPrice <= 0) {
            ctx.addIssue({
                path: ["limitPrice"],
                message: "El límite debe ser mayor a 0",
                code: z.ZodIssueCode.custom
            });
        }
        if (data.value <= 0) {
            ctx.addIssue({
                path: ["value"],
                message: "El valor debe ser mayor a 0",
                code: z.ZodIssueCode.custom
            });
        }
    }
});

export type VariableOrderFormData = z.infer<typeof schema>;

export const orderRepository = new OrdersApiRepository();

export const variableOrderKeys = {
    all: ['variable_orders'] as const,
    details: (id: string) => [...variableOrderKeys.all, id] as const,
    allInstruments: ['instrument'] as const,
    allShares: ['shares'] as const,
};